import { TableModal } from 'components/Modals/TableModal/TableModal';
import { FieldArray, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { IQuestion } from 'types/Question.types';
import {
  Container,
  FormContainer,
  ModelNotFoundLabel,
  LabelsDivider,
  LabelsWrapper,
} from './ConfigureForm.styled';
import Button from 'components/Button/Button';
import { ArrowCounterClockwise, FileText, Stack } from '@phosphor-icons/react';
import QueryProvider from '../../../providers/QueryProvider/QueryProvider';
import {
  useGetSortedQuestions,
  useGetValuesFromQuestionOutputs,
  useHandleNavigateToBomPage,
  useListenForSubmitConfigurationTrigger,
  useScrollToFirstIndicativeQuestion,
} from './hooks';
import { useQuotationLineResolveParams } from 'utils/hooks/useQuotationLineResolveParams';
import { useTemplateConfigureParams } from 'utils/hooks/useTemplateConfigureParams';
import OverlayProvider from 'providers/OverlayProvider/OverlayProvider';
import { resetConfigureForm } from 'store/Model/actions/model';
import {
  resetManuallyChangedQuestionNames,
  resetManuallyClearedQuestionNames,
  resetTouchedQuestionNames,
  setAreAllFieldsTouched,
  setIncludeBomItems,
  setIsFormValid,
} from 'store/Form/actions/form';
import { extractQuestionPrice, questionHasValue } from './helpers';
import { useTranslation } from 'react-i18next';
import QuestionSkeletons from './QuestionSkeletons/QuestionSkeletons';
import { ColorModal } from 'components/Modals/ColorModal/ColorModal';
import { AppMode } from 'store/Common/reducers/common';
import { QuestionRow } from './QuestionRow/QuestionRow';
import { ConfigurationQuestionsRows } from './ConfigurationQuestionsRows/ConfigurationQuestionsRows';
import { TemplateSelectRow } from './TemplateSelectRow/TemplateSelectRow';
import {
  setClearedExtraPriceQuestionSlugs,
  setIndicativeQuestionKeys,
  setSelectedConfigurationAnswers,
  setSelectedTemplate,
} from 'store/SubmitConfiguration/actions/submitConfiguration';
import {
  IConfigurationQuestion,
  ISelectedConfigurationAnswer,
} from 'types/ConfigurationQuestion.types';
import { hasAnySpecVariable } from 'pages/SpecsPage/helpers';
import { useIsVeranoCompany } from 'utils/hooks/useIsVeranoCompany';
import { useNavigateWithQuery } from 'utils/hooks/useNavigateWithQuery';
import { RoutesConfig } from 'navigation/routes';
import { buildConfiguratorSubPageState } from 'navigation/helpers';
import {
  rememberConfiguratorScroll,
  useRestoreConfiguratorScroll,
} from 'utils/hooks/useConfiguratorScrollMemory';
import { useLocation } from 'react-router-dom';

interface IConfigureFormProps {
  hasImages: boolean;
}

const ConfigureForm = ({ hasImages }: IConfigureFormProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const valuesFromOutputs = useGetValuesFromQuestionOutputs();

  const questions: IQuestion[] = useGetSortedQuestions();
  const hasBomItems = useSelector(
    (state: IRootReducerState) => state.modelInfo.hasBomItems
  );

  // Variables arrive with the query response, so unlike BOM items there is
  // nothing to request before showing them - the label is here whenever the
  // model has any V_ variables. Selecting the boolean rather than the variables
  // object keeps the form from re-rendering on every query.
  const hasSpecVariables = useSelector((state: IRootReducerState) =>
    hasAnySpecVariable(state.modelInfo.variables)
  );
  const isVeranoCompany = useIsVeranoCompany();

  const { queryStatus, pricing, extraPriceQuestions } = useSelector(
    (state: IRootReducerState) => state.modelInfo
  );
  const { isFormValid, modelNotFound } = useSelector(
    (state: IRootReducerState) => state.formInfo
  );

  const selectedConfigurationAnswers = useSelector(
    (state: IRootReducerState) =>
      state.submitConfigurationInfo.selectedConfigurationAnswers
  );

  // Block submission while any active required extra price question is unanswered.
  const areRequiredConfigurationQuestionsAnswered = (
    (extraPriceQuestions || []) as IConfigurationQuestion[]
  )
    .filter((question: IConfigurationQuestion) => question.is_required)
    .every((question: IConfigurationQuestion) =>
      selectedConfigurationAnswers.some(
        (answer: ISelectedConfigurationAnswer) =>
          answer.questionSlug === question.slug
      )
    );

  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const modelId = useSelector((state: IRootReducerState) => state.modelInfo.id);

  useListenForSubmitConfigurationTrigger(valuesFromOutputs, dispatch);

  const isResolvingQuotationLine = !!useQuotationLineResolveParams();
  const isTemplateMode = !!useTemplateConfigureParams();
  useScrollToFirstIndicativeQuestion(!!questions.length);
  // Declared after the indicative scroll so that hook can see the pending
  // offset and stand aside before this one consumes it.
  useRestoreConfiguratorScroll(modelId, !!questions.length);

  const { setHasJustClickedIncludeBomItems } = useHandleNavigateToBomPage();
  const navigateWithQuery = useNavigateWithQuery();
  const location = useLocation();

  // Supplier and external (Navision) only
  const showSpecsButton =
    isVeranoCompany &&
    hasSpecVariables &&
    [AppMode.SUPPLIER, AppMode.EXTERNAL].includes(appMode);
  // Clearing every answer would also drop the indicative marks the user is
  // here to resolve.
  const showClearButton = !isResolvingQuotationLine;

  return (
    <Container data-testid={'form-container'} hasImages={hasImages}>
      <OverlayProvider>
        <Formik
          enableReinitialize
          initialValues={valuesFromOutputs}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={() => {
            return;
          }}
          validateOnMount={true}
        >
          {({ isValid, values, setFormikState }) => {
            if (
              queryStatus.success &&
              JSON.stringify(values) !== JSON.stringify(valuesFromOutputs)
            ) {
              // enableReinitialize doesnt trigger every time when the formik context values and the values from outputs missmatch
              // These objects can missmatch after the query request is successful and new form data is calculated from the response
              // This will update the formik values to match the query response data and rerender the form
              setFormikState((state) => ({
                ...state,
                values: valuesFromOutputs,
              }));
            }

            // Required extra price questions are hidden in template mode, so
            // gating on them would leave the save button disabled with no visible cause.
            const effectiveFormValid =
              isValid &&
              (isTemplateMode || areRequiredConfigurationQuestionsAnswered);
            if (isFormValid !== effectiveFormValid) {
              dispatch(setIsFormValid(effectiveFormValid));
            }

            let nonHiddenQuestionIndex =
              !isTemplateMode && extraPriceQuestions?.length ? -1 : 0;
            return (
              <Form>
                <LabelsWrapper>
                  {showSpecsButton ? (
                    <Button
                      secondary
                      label={t('Specs')}
                      icon={<FileText size={16} />}
                      pwId="view-specs-button"
                      onClick={() => {
                        rememberConfiguratorScroll(modelId);
                        navigateWithQuery(RoutesConfig.Specs.fullPath, {
                          state: buildConfiguratorSubPageState(location),
                        });
                      }}
                    />
                  ) : null}

                  {hasBomItems ? (
                    <Button
                      secondary
                      label={t('BOM')}
                      icon={<Stack size={16} />}
                      pwId="view-bom-items-button"
                      onClick={() => {
                        dispatch(setIncludeBomItems(true));
                        setHasJustClickedIncludeBomItems(true);
                      }}
                    />
                  ) : null}

                  {/* Only earns its place once it has something to separate. */}
                  {(showSpecsButton || hasBomItems) && showClearButton ? (
                    <LabelsDivider />
                  ) : null}

                  {showClearButton ? (
                    <Button
                      redOutline
                      label={t('Clear')}
                      icon={<ArrowCounterClockwise size={16} />}
                      pwId="clear-all-fields-button"
                      onClick={() => {
                        dispatch(resetConfigureForm());
                        dispatch(resetTouchedQuestionNames());
                        dispatch(resetManuallyChangedQuestionNames());
                        dispatch(resetManuallyClearedQuestionNames());
                        dispatch(setAreAllFieldsTouched(false));
                        dispatch(setSelectedConfigurationAnswers([]));
                        dispatch(setClearedExtraPriceQuestionSlugs([]));
                        dispatch(setIndicativeQuestionKeys([]));
                        dispatch(setSelectedTemplate(null));
                      }}
                    />
                  ) : null}
                </LabelsWrapper>
                <TemplateSelectRow />
                {modelNotFound && (
                  <ModelNotFoundLabel>
                    {t('Model not found')}
                  </ModelNotFoundLabel>
                )}
                <FieldArray
                  name="questions"
                  render={() => (
                    <QueryProvider>
                      <FormContainer>
                        {questions.length ? (
                          <>
                            {questions?.map(
                              (question: IQuestion, index: number) => {
                                if (!question.hidden) {
                                  nonHiddenQuestionIndex++;
                                }
                                const showBorder =
                                  nonHiddenQuestionIndex <=
                                  questions.filter(
                                    (question: IQuestion) => !question.hidden
                                  ).length -
                                    1;

                                const questionPrice = extractQuestionPrice(
                                  pricing,
                                  question.initialKey,
                                  appMode !== AppMode.EXTERNAL
                                );
                                const hasValue = questionHasValue(
                                  values,
                                  question
                                );
                                return (
                                  <QuestionRow
                                    key={`${question.initialKey}-${index}`}
                                    showBorder={showBorder}
                                    question={question}
                                    hasValue={hasValue}
                                    hasImages={hasImages}
                                    questionPrice={questionPrice}
                                  />
                                );
                              }
                            )}
                            {isTemplateMode ? null : (
                              <ConfigurationQuestionsRows />
                            )}
                          </>
                        ) : (
                          <QuestionSkeletons />
                        )}
                      </FormContainer>
                      <TableModal />
                      <ColorModal />
                    </QueryProvider>
                  )}
                />
              </Form>
            );
          }}
        </Formik>
      </OverlayProvider>
    </Container>
  );
};

export default ConfigureForm;
